import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static targets = [
    "monthSelector",
    "yearSelector",
    "weekSelector",
    "addressField",
    "meetLinkField",
    "meetLinkInput",
    "dateField",
    "outOfOfficeDates",
    "startTime",
    "endTime",
    "startHour",
    "startMinute",
    "endHour",
    "endMinute",
    "headerDateTime",
    "titleField",
    "moreInfoIcon",
    "paymentBillingIcon"
  ]

  connect() {
    this.initializeTooltips()

    // Inicializar manejo de precios
    this.initializePriceHandling()

    // Solo inicializar la ubicación si estamos en un formulario de cita
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')
    if (locationInput) {
      this.updateLocation({ target: { dataset: { location: locationInput.value } } })
    }

    // Inicializar restricciones de tiempo si estamos en un formulario de cita
    if (this.hasStartHourTarget && this.hasEndHourTarget &&
        this.hasStartMinuteTarget && this.hasEndMinuteTarget &&
        this.hasStartTimeTarget && this.hasEndTimeTarget) {
      this.initializeTimeRestrictions()
      this.validateStartTime()
      this.validateEndTime()

      // Ajustar la hora de fin inicial si está deshabilitada
      this.adjustInitialEndTime()
    }

    // Inicializar validación de dirección
    if (this.hasAddressFieldTarget) {
      this.validateAddress()
    }

    // Solo actualizar el encabezado si estamos en un formulario de cita
    if (this.hasDateFieldTarget && this.hasStartHourTarget &&
        this.hasStartMinuteTarget && this.hasHeaderDateTimeTarget) {
      this.updateHeaderDateTime()
    }

    this.updateHeaderDateTime()

    // Inicializar los botones de agregar cita en espacios libres
    // Solo si no se ha inicializado ya
    if (!this.addButtonsInitialized) {
      this.initializeAddButtons()
      this.addButtonsInitialized = true
    }

    // Agregar validación al picker de fechas
    this.initializeDatePickerValidation()

    // Inicializar iconos de secciones colapsables
    this.initializeCollapsibleIcons()

    // Handle auto-open modal from patient creation and preselection
    if (this.element.querySelector('[data-appointments-auto-open]')) {
      this.autoOpenModalFromPatientCreation()
    }

    // Handle auto-open invoice modal
    if (this.element.querySelector('[data-appointments-auto-open-invoice]')) {
      this.autoOpenInvoiceModal()
    }

    // Initialize time selectors
    this.initializeTimeSelectors()
  }

  // Initialize time selectors when the form loads
  initializeTimeSelectors() {
    const startHourSelect = document.querySelector('[data-appointments-target="startHour"]')
    const endHourSelect = document.querySelector('[data-appointments-target="endHour"]')

    if (startHourSelect) {
      const startEvent = new Event('change')
      startHourSelect.dispatchEvent(startEvent)
    }

    if (endHourSelect) {
      const endEvent = new Event('change')
      endHourSelect.dispatchEvent(endEvent)
    }
  }

  initializeTooltips() {
    // Use jQuery for Bootstrap 4.5.2 tooltip initialization
    try {
      if (typeof $ !== 'undefined') {
        $('[data-toggle="tooltip"]').tooltip({
          container: 'body'
        })
      } else if (bootstrap && bootstrap.Tooltip) {
        // Fallback to direct Bootstrap if jQuery isn't available
        const tooltips = document.querySelectorAll('[data-toggle="tooltip"]')
        tooltips.forEach(tooltip => {
          new bootstrap.Tooltip(tooltip)
        })
      }
    } catch (e) {
      console.error('Error initializing tooltips:', e)
    }
  }

  initializeTimeRestrictions() {

    // Guardar la hora original en modo edición
    if (this.hasStartHourTarget && this.hasStartMinuteTarget) {
      const startHour = this.startHourTarget.value
      const startMinute = this.startMinuteTarget.value
      this.startTimeTarget.dataset.originalTime = `${startHour}:${startMinute}`
    }

    if (this.hasEndHourTarget && this.hasEndMinuteTarget) {
      const endHour = this.endHourTarget.value
      const endMinute = this.endMinuteTarget.value
      this.endTimeTarget.dataset.originalTime = `${endHour}:${endMinute}`
    }

    // Actualizar campos ocultos cuando cambian las horas o minutos
    this.updateHiddenTimeFields()
  }

  updateHiddenTimeFields() {
    if (this.hasStartHourTarget && this.hasStartMinuteTarget) {
      const startHour = this.startHourTarget.value
      const startMinute = this.startMinuteTarget.value
      this.startTimeTarget.value = `${startHour}:${startMinute}`
    }

    if (this.hasEndHourTarget && this.hasEndMinuteTarget) {
      const endHour = this.endHourTarget.value
      const endMinute = this.endMinuteTarget.value
      this.endTimeTarget.value = `${endHour}:${endMinute}`
    }
  }

  validateStartTime() {
    if (!this.hasStartTimeTarget || !this.hasEndTimeTarget) return

    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()

    // Solo ajustar automáticamente la hora de fin si NO estamos en modo de edición
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    if (!isEditMode) {
      // Ajustar automáticamente la hora de fin según la duración del paciente
      // Buscar el controlador de patient-search para obtener la información del paciente
      const patientSearchController = this.application.getControllerForElementAndIdentifier(
        this.element.querySelector('[data-controller*="patient-search"]'),
        'patient-search'
      )
      let durationMinutes = 30;
      if (patientSearchController) {
        const patientId = patientSearchController.hiddenInputTarget.value
        if (patientId) {
          const firstAppointmentBadge = patientSearchController.firstAppointmentBadgeTarget
          const isFirstAppointment = firstAppointmentBadge && !firstAppointmentBadge.classList.contains('d-none')
          durationMinutes = isFirstAppointment ? 45 : 30
        }
      }
      this.adjustEndTime(durationMinutes)
    }
  }

  validateEndTime() {
    if (!this.hasStartTimeTarget || !this.hasEndTimeTarget) return

    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()
  }

  adjustInitialEndTime() {
    const endHour = this.endHourTarget.value
    const endMinute = this.endMinuteTarget.value

    if (this.endHourTarget.selectedOptions[0].disabled) {
      // Intentar con 15 minutos antes
      let newMinute = parseInt(endMinute) - 15
      let newHour = parseInt(endHour)

      if (newMinute < 0) {
        newMinute = 45
        newHour -= 1
      }

      // Buscar la primera hora disponible
      while (newHour >= 8) {
        const hourOption = this.endHourTarget.querySelector(`option[value="${newHour.toString().padStart(2, '0')}"]`)
        if (hourOption && !hourOption.disabled) {
          hourOption.selected = true
          this.endHourTarget.dispatchEvent(new Event('change'))

          // Buscar el primer minuto disponible en esa hora
          const minuteOption = this.endMinuteTarget.querySelector(`option[value="${newMinute.toString().padStart(2, '0')}"]`)
          if (minuteOption && !minuteOption.disabled) {
            minuteOption.selected = true
          } else {
            // Si el minuto está deshabilitado, buscar el primer minuto disponible
            const firstAvailableMinute = this.endMinuteTarget.querySelector('option:not([disabled])')
            if (firstAvailableMinute) {
              firstAvailableMinute.selected = true
            }
          }
          break
        }
        newMinute = 45
        newHour -= 1
      }
    }
  }

  validateForm(event) {
    const startTime = this.startTimeTarget.value
    const endTime = this.endTimeTarget.value
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked') || document.querySelector('input[name="location"]:checked')
    const addressInput = this.hasAddressFieldTarget ? this.addressFieldTarget : null
    const isEditable = !document.querySelector('select[name="start_hour"]').disabled

    // Verificar si estamos en modo de edición
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    // Verificar si el usuario está intentando generar una factura AFIP
    const isGeneratingInvoice = event.submitter && (
      event.submitter.classList.contains('btn-invoice-afip') ||
      event.submitter.textContent.includes('Generar factura AFIP') ||
      event.submitter.getAttribute('data-action')?.includes('afip-invoice')
    )

    if (!isEditable) {
      return true
    }

    if (!startTime || !endTime) {
      alert('Por favor, complete ambos campos de hora')
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // Validar dirección si es presencial y el formulario es editable
    if (isEditable && locationInput && locationInput.value === 'Presencial') {
      if (!addressInput || !addressInput.value || addressInput.value.trim() === '') {
        alert('Por favor, ingrese la dirección para la cita presencial')
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    // Solo validar campos AFIP si se está generando una factura
    if (isGeneratingInvoice) {
      const baseDescription = document.querySelector('textarea[name="appointment[base_description]"]')
      const diagnosisHealthInsurance = document.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')

      if (baseDescription && (!baseDescription.value || baseDescription.value.trim() === '')) {
        alert('Por favor, complete la descripción de la consulta para generar la factura AFIP')
        event.preventDefault()
        event.stopPropagation()
        return false
      }

      if (diagnosisHealthInsurance && (!diagnosisHealthInsurance.value || diagnosisHealthInsurance.value.trim() === '')) {
        alert('Por favor, complete el diagnóstico y prepaga para generar la factura AFIP')
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    // Convertir las horas a minutos para facilitar la comparación
    const [startHours, startMinutes] = startTime.split(':').map(Number)
    const [endHours, endMinutes] = endTime.split(':').map(Number)

    const startTotalMinutes = startHours * 60 + startMinutes
    const endTotalMinutes = endHours * 60 + endMinutes

    let hasError = false

    // Validar rango de horas (8:00 - 24:00)
    if (startTotalMinutes < 8 * 60 || startTotalMinutes > 24 * 60) {
      alert('Las horas disponibles son de 8:00 a 24:00')
      hasError = true
    }

    if (endTotalMinutes < 8 * 60 || endTotalMinutes > 24 * 60) {
      alert('Las horas disponibles son de 8:00 a 24:00')
      hasError = true
    }

    // Validar orden de las horas
    if (startTotalMinutes >= endTotalMinutes) {
      alert('La hora de inicio debe ser anterior a la hora de fin')
      hasError = true
    }

    // Solo validar conflictos si NO estamos en modo de edición
    if (!isEditMode) {
      // Validar que no se solape con eventos existentes
      const startHourOption = this.startHourTarget.selectedOptions[0]
      const endHourOption = this.endHourTarget.selectedOptions[0]
      const startBookedMinutes = JSON.parse(startHourOption.dataset.bookedMinutes || '[]')
      const endBookedMinutes = JSON.parse(endHourOption.dataset.bookedMinutes || '[]')

      // Verificar si la hora de inicio está ocupada
      const startTimeOccupied = startBookedMinutes.some(slot =>
        slot.minute === startMinutes && (!slot.is_end || slot.is_start)
      )

      // Verificar si la hora de fin está ocupada
      const endTimeOccupied = endBookedMinutes.some(slot =>
        slot.minute === endMinutes && (!slot.is_start || slot.is_end)
      )

      // Verificar si hay eventos en el medio
      let hasConflictInMiddle = false
      let currentHour = startHours
      let currentMinute = startMinutes

      while (currentHour < endHours || (currentHour === endHours && currentMinute < endMinutes)) {
        const hourOption = this.startHourTarget.querySelector(`option[value="${currentHour.toString().padStart(2, '0')}"]`)
        if (hourOption) {
          const bookedMinutes = JSON.parse(hourOption.dataset.bookedMinutes || '[]')
          const hasConflict = bookedMinutes.some(slot => {
            const slotTotalMinutes = currentHour * 60 + slot.minute
            return slotTotalMinutes > startTotalMinutes &&
                   slotTotalMinutes < endTotalMinutes &&
                   (!slot.is_end || slot.is_start)
          })

          if (hasConflict) {
            hasConflictInMiddle = true
            break
          }
        }

        currentMinute += 15
        if (currentMinute >= 60) {
          currentMinute = 0
          currentHour += 1
        }
      }

      if (startTimeOccupied) {
        alert('La hora de inicio ya se encuentra ocupada. Por favor elija otra hora.')
        hasError = true
      }

      if (endTimeOccupied) {
        alert('La hora de fin ya se encuentra ocupada. Por favor elija otra hora.')
        hasError = true
      }

      if (hasConflictInMiddle) {
        alert('El rango de tiempo seleccionado incluye horas que ya están ocupadas. Por favor elija otro rango.')
        hasError = true
      }
    }

    if (hasError) {
      // Restaurar horas originales asegurándonos de que sean válidas
      if (this.startTimeTarget.dataset.originalTime) {
        const [originalHour, originalMinute] = this.startTimeTarget.dataset.originalTime.split(':')
        const hourOption = this.startHourTarget.querySelector(`option[value="${originalHour}"]`)
        if (hourOption && !hourOption.disabled) {
          this.startHourTarget.value = originalHour
          this.startMinuteTarget.value = originalMinute
        } else {
          // Si la hora original no es válida, buscar la primera hora disponible
          const firstAvailableHour = this.startHourTarget.querySelector('option:not([disabled])')
          if (firstAvailableHour) {
            firstAvailableHour.selected = true
            this.startHourTarget.dispatchEvent(new Event('change'))
          }
        }
      }

      if (this.endTimeTarget.dataset.originalTime) {
        const [originalHour, originalMinute] = this.endTimeTarget.dataset.originalTime.split(':')
        const hourOption = this.endHourTarget.querySelector(`option[value="${originalHour}"]`)
        if (hourOption && !hourOption.disabled) {
          this.endHourTarget.value = originalHour
          this.endMinuteTarget.value = originalMinute
        } else {
          // Si la hora original no es válida, buscar la primera hora disponible
          const firstAvailableHour = this.endHourTarget.querySelector('option:not([disabled])')
          if (firstAvailableHour) {
            firstAvailableHour.selected = true
            this.endHourTarget.dispatchEvent(new Event('change'))
          }
        }
      }

      // Actualizar campos ocultos con los valores originales
      this.updateHiddenTimeFields()

      event.preventDefault()
      event.stopPropagation()
      return false
    }

    return true
  }

  // ==============================================
  // ACCIONES DE NAVEGACIÓN
  // ==============================================

  updateWeekSelector() {
    const year = parseInt(this.yearSelectorTarget.value)
    const month = parseInt(this.monthSelectorTarget.value)

    const startDate = new Date(year, month - 1, 1)
    const endDate = new Date(year, month, 0)

    const weeksSet = new Set()

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const weekNumber = this.getWeekNumber(d)
      weeksSet.add(weekNumber)
    }

    const weeks = Array.from(weeksSet).sort((a, b) => a - b)

    // Limpiar selector actual
    this.weekSelectorTarget.innerHTML = ""

    weeks.forEach(week => {
      const startOfWeek = this.getMondayOfWeek(year, week)
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 4)

      const label = `Semana ${startOfWeek.getDate().toString().padStart(2, '0')}/${(startOfWeek.getMonth()+1).toString().padStart(2, '0')} al ${endOfWeek.getDate().toString().padStart(2, '0')}/${(endOfWeek.getMonth()+1).toString().padStart(2, '0')}`

      const option = document.createElement("option")
      option.value = week
      option.textContent = label

      this.weekSelectorTarget.appendChild(option)
    })

    // Si solo hay una semana disponible, actualizar automáticamente
    if (weeks.length === 1) {
      // Disparar evento de cambio para actualizar el calendario
      const event = new Event('change')
      this.weekSelectorTarget.dispatchEvent(event)
    }
  }

  getWeekNumber(date) {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = tempDate.getUTCDay() || 7
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1))
    return Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7)
  }

  getMondayOfWeek(year, week) {
    const simple = new Date(year, 0, 1 + (week - 1) * 7)
    const dow = simple.getDay()
    const monday = new Date(simple)
    if (dow <= 4)
      monday.setDate(simple.getDate() - simple.getDay() + 1)
    else
      monday.setDate(simple.getDate() + 8 - simple.getDay())
    return monday
  }

  // ==============================================
  // ACCIONES DE MODAL
  // ==============================================

  openModal(event) {
    event.preventDefault()
    event.stopPropagation() // Prevent event bubbling

    // First, ensure any existing modal and backdrop are properly cleaned up
    this.closeModal(event)

    // Show loading spinner
    const loadingSpinner = document.getElementById('loading_spinner')
    if (loadingSpinner) {
      loadingSpinner.classList.remove('d-none')
    }

    // Disable all calendar cells to prevent multiple clicks
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      cell.style.pointerEvents = 'none'
    })

    const modalElement = document.getElementById("appointment_modal")
    const date = event.currentTarget.dataset.date
    const hour = event.currentTarget.dataset.hour
    const doctorEmail = event.currentTarget.dataset.doctorEmail
    const calendarId = event.currentTarget.dataset.calendarId

    // Construir URL con parámetros
    const url = `/appointments/new?date=${date}&hour=${hour}&doctor_email=${doctorEmail}&calendar_id=${calendarId}`

    this.loadModalContent(url)
  }

  editAppointment(event) {
    event.preventDefault()
    event.stopPropagation() // Prevent event bubbling
    const modalElement = document.getElementById("appointment_modal")

    // Verificamos si el modal ya está abierto
    const modalIsShown = modalElement.classList.contains("show")
    if (modalIsShown) {
      return
    }

    // Show loading spinner
    const loadingSpinner = document.getElementById('loading_spinner')
    if (loadingSpinner) {
      loadingSpinner.classList.remove('d-none')
    }

    // Disable all calendar cells to prevent multiple clicks
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      cell.style.pointerEvents = 'none'
    })

    const appointmentId = event.currentTarget.dataset.appointmentId
    const calendarId = event.currentTarget.closest('.calendar-cell').dataset.calendarId
    const doctorEmail = event.currentTarget.closest('.calendar-cell').dataset.doctorEmail

    // Construir URL para editar
    const url = `/appointments/${appointmentId}/edit?calendar_id=${calendarId}&doctor_email=${doctorEmail}`

    this.loadModalContent(url)
  }

  loadModalContent(url) {
    // Limpiar cualquier backdrop existente antes de cargar nuevo contenido
    const existingBackdrops = document.querySelectorAll('.modal-backdrop')
    existingBackdrops.forEach(backdrop => backdrop.remove())
    document.body.classList.remove('modal-open')
    document.body.style.paddingRight = '0'

    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        // Insertar contenido en el modal (dentro del div modal_content)
        const modalContent = document.getElementById("modal_content")
        modalContent.innerHTML = html

        // Manejar el toggle de secciones colapsables ("Más información" y "Pago y Facturación")
        const collapseButtons = modalContent.querySelectorAll('[data-toggle="collapse"]')
        collapseButtons.forEach(button => {
          const targetId = button.getAttribute('data-target')
          const content = targetId ? document.querySelector(targetId) : null

          if (content) {
            // Remover los atributos de Bootstrap collapse
            button.removeAttribute('data-toggle')
            button.removeAttribute('data-target')
            button.removeAttribute('aria-expanded')
            button.removeAttribute('aria-controls')

            // Ocultar el contenido inicialmente
            content.style.display = 'none'

            // Manejar el click del botón
            button.addEventListener('click', () => {
              const isVisible = content.style.display !== 'none'
              content.style.display = isVisible ? 'none' : 'block'

              // Actualizar el ícono
              const icon = button.querySelector('i')
              if (icon) {
                icon.classList.toggle('fa-plus-circle')
                icon.classList.toggle('fa-minus-circle')
              }

              // Cambiar el texto del botón solo para "Más información"
              if (targetId === '#moreInfo') {
                const buttonText = button.querySelector('span')
                if (buttonText) {
                  buttonText.textContent = isVisible ? 'Más información' : 'Menos información'
                }
              }
            })
          }
        })

        // Obtener el elemento modal
        const modalElement = document.getElementById("appointment_modal")

        // Actualizar el encabezado con la fecha y hora iniciales
        const dateField = document.querySelector('[data-target="appointments.dateField"]')
        const startHour = document.querySelector('[data-target="appointments.startHour"]')
        const startMinute = document.querySelector('[data-target="appointments.startMinute"]')

        if (dateField && startHour && startMinute) {
          const dateObj = new Date(dateField.value)
          const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
          const dayName = dayNames[dateObj.getDay()]
          const day = dateObj.getDate()
          const time = `${startHour.value}:${startMinute.value}`

          const headerDateTime = document.querySelector('[data-target="appointments.headerDateTime"]')
          if (headerDateTime) {
            headerDateTime.textContent = `${dayName} ${day} - ${time}hs`
          }
        }

        // Crear nueva instancia del modal directamente
        try {
          // Asegurarse de que no haya instancias previas del modal
          const existingModal = bootstrap.Modal.getInstance(modalElement)
          if (existingModal) {
            existingModal.dispose()
          }

          const modal = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          })

          // Mostrar el modal
          modal.show()

          // Remover el padding-right que Bootstrap agrega al body
          document.body.style.paddingRight = '0'
        } catch (e) {
          // Si hay error con Bootstrap, usar enfoque manual
          modalElement.style.display = 'block'
          modalElement.classList.add('show')
          modalElement.setAttribute('aria-hidden', 'false')
          document.body.classList.add('modal-open')
          document.body.style.paddingRight = '0'

          // Crear backdrop manualmente si es necesario
          if (!document.querySelector('.modal-backdrop')) {
            const backdrop = document.createElement('div')
            backdrop.classList.add('modal-backdrop', 'fade', 'show')
            document.body.appendChild(backdrop)
          }
        }

        // Hide loading spinner and re-enable calendar cells
        const loadingSpinner = document.getElementById('loading_spinner')
        if (loadingSpinner) {
          loadingSpinner.classList.add('d-none')
        }
        const calendarCells = document.querySelectorAll('.calendar-cell')
        calendarCells.forEach(cell => {
          cell.style.pointerEvents = 'auto'
        })

        // Manual initialization for dynamically loaded content
        this.initializeLocationFields()

        // Initialize time selectors
        this.initializeTimeSelectors()

        // Add event listeners for modal closing to cleanup URL parameters
        this.addModalCloseListeners()
      })
      .catch(error => {
        console.error("Error al cargar modal:", error)
        // Hide loading spinner and re-enable calendar cells even on error
        const loadingSpinner = document.getElementById('loading_spinner')
        if (loadingSpinner) {
          loadingSpinner.classList.add('d-none')
        }
        const calendarCells = document.querySelectorAll('.calendar-cell')
        calendarCells.forEach(cell => {
          cell.style.pointerEvents = 'auto'
        })
      })
  }

  closeModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation() // Prevent event bubbling
    }

    // Obtener el elemento modal
    const modalElement = document.getElementById("appointment_modal")

    // Intentar cerrarlo con Bootstrap si está disponible
    try {
      const modal = bootstrap.Modal.getInstance(modalElement)
      if (modal) {
        modal.hide()
      }
    } catch (e) {
      // Si hay error con Bootstrap, usar enfoque manual
    }

    // Asegurarse de que el modal esté cerrado
    modalElement.classList.remove('show')
    modalElement.setAttribute('aria-hidden', 'true')
    modalElement.style.display = 'none'

    // Eliminar todos los backdrops existentes
    const backdrops = document.querySelectorAll('.modal-backdrop')
    backdrops.forEach(backdrop => backdrop.remove())

    // Restaurar el scroll del body
    document.body.classList.remove('modal-open')
    document.body.style.overflow = ''
    document.body.style.paddingRight = '0'

    // Only cleanup URL parameters if this is a real close action (not from openModal)
    if (!event || !event.currentTarget || !event.currentTarget.dataset.date) {
      this.cleanupUrlParameters()
    }
  }

  // Clean up URL parameters when modal is closed
  cleanupUrlParameters() {
    const currentUrl = new URL(window.location)
    const params = currentUrl.searchParams

    // Keep only essential parameters
    const essentialParams = ['date', 'doctor_email', 'calendar_id']
    const paramsToRemove = []

    // Find parameters to remove
    for (const [key, value] of params.entries()) {
      if (!essentialParams.includes(key)) {
        paramsToRemove.push(key)
      }
    }

    // Remove non-essential parameters
    paramsToRemove.forEach(param => {
      params.delete(param)
    })

    // Update URL without page reload
    const newUrl = currentUrl.pathname + (params.toString() ? '?' + params.toString() : '')
    window.history.replaceState({}, '', newUrl)
  }

  updateLocation(event) {
    const locationType = event.target.dataset.location
    const meetLinkInput = this.hasMeetLinkInputTarget ? this.meetLinkInputTarget : document.querySelector('input[name="appointment[meet_link]"]')
    const addressField = document.getElementById('addressField')
    const addressInput = addressField?.querySelector('input')
    const meetLinkField = document.getElementById('meetLinkField')

    // Store the original meet link if it exists (for edit mode)
    const originalMeetLink = meetLinkInput?.value || ''
    const hasOriginalMeetLink = originalMeetLink.includes('meet.google.com')

    // Get the original address from data attributes
    const defaultAddress = addressInput?.dataset?.defaultAddress || 'Dirección no configurada'
    const originalAddress = addressInput?.dataset?.originalAddress || defaultAddress

    if (locationType === 'Virtual') {
      // Clear and hide address field
      if (addressField) {
        addressField.style.display = 'none'
        if (addressInput) {
          addressInput.value = ''
          addressInput.required = false
          addressInput.disabled = true
        }
      }

      // Show meet link field
      if (meetLinkField) {
        meetLinkField.style.display = 'block'
      }

      const meetLinkText = meetLinkField?.querySelector('#meetLinkText')

      if (meetLinkText) {
        if (hasOriginalMeetLink) {
          // Edit mode: restore existing link
          meetLinkText.innerHTML = `<a href="${originalMeetLink}" target="_blank">${originalMeetLink}</a>`
          meetLinkInput.value = originalMeetLink
        } else {
          // New mode or no existing link: show message
          meetLinkText.textContent = 'El link de Google Meet se creará automáticamente al guardar la cita'
          meetLinkInput.value = 'virtual_appointment'
        }
      }
    } else {
      // Presencial
      if (addressField) {
        addressField.style.display = 'block'
        if (addressInput) {
          // Always restore the original address when switching back to presencial
          addressInput.value = originalAddress
          addressInput.required = true
          addressInput.disabled = false
        }
      }

      if (meetLinkField) {
        meetLinkField.style.display = 'none'
      }

      // Clear meet link input but preserve original for potential switch back
      if (meetLinkInput) {
        // Only clear if it's not a real meet link (preserve original links)
        if (!hasOriginalMeetLink) {
          meetLinkInput.value = ''
        }
      }
    }

    // Validate address when location changes
    this.validateAddress()
  }

  validateAddress() {
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')

    // Only proceed if we have the addressField target
    if (!this.hasAddressFieldTarget) {
      return
    }

    const addressInput = this.addressFieldTarget

    if (locationInput && locationInput.value === 'Presencial') {
      if (!addressInput.value || addressInput.value.trim() === '') {
        addressInput.setCustomValidity('La dirección es requerida para citas presenciales')
      } else {
        addressInput.setCustomValidity('')
      }
    } else {
      addressInput.setCustomValidity('')
    }
  }

  generateMeetLink() {
    const meetingId = Math.random().toString(36).substring(2, 15)
    return `https://meet.google.com/${meetingId}`
  }

  validateDate(event) {
    const blockedDates = JSON.parse(this.outOfOfficeDatesTarget.value || '[]')

    // El input date ya trae "YYYY-MM-DD": no pasarlo por new Date() y toISOString(),
    // que con el campo vacío tira RangeError.
    const selectedDateStr = event.target.value
    const isBlocked = blockedDates.some(date => date === selectedDateStr)

    if (isBlocked) {
      event.preventDefault()
      // Set the value to the original value before the change
      event.target.value = event.target.dataset.originalValue

      // Determinar si es fin de semana o evento de todo el día
      // Crear la fecha de manera más explícita para evitar problemas de zona horaria
      const [year, month, day] = event.target.value.split('-').map(Number)
      const dateForWeekday = new Date(year, month - 1, day) // month - 1 porque JavaScript cuenta meses desde 0
      const dayOfWeek = dateForWeekday.getDay() // 0 = domingo, 6 = sábado

      // Verificar si es fin de semana (0 = domingo, 6 = sábado)
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

      if (isWeekend) {
        alert('No se pueden programar citas los sábados y domingos.')
      } else {
        alert('No se pueden programar citas en días que tienen eventos de todo el día.')
      }
      return
    }

    this.updateHeaderDateTime()

    // Immediately clear booked slots so all time options are enabled while
    // the async fetch to Google Calendar is in progress. Without this,
    // disabled minutes from the PREVIOUS date remain visible and prevent
    // selection (e.g. 11:15 blocked by another event on the old date).
    this.clearBookedSlots()

    this.fetchBookedSlotsForDate(event.target.value)
  }

  updateHeaderDateTime() {
    // Verificar que todos los targets necesarios existen
    if (!this.hasDateFieldTarget || !this.hasStartHourTarget || !this.hasStartMinuteTarget || !this.hasHeaderDateTimeTarget) {
      return
    }

    const date = this.dateFieldTarget.value
    const startHour = this.startHourTarget.value
    const startMinute = this.startMinuteTarget.value

    if (date && startHour && startMinute) {
      // Crear la fecha con la zona horaria local
      const [year, month, dayOfMonth] = date.split('-').map(Number)
      const dateObj = new Date(year, month - 1, dayOfMonth)
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
      const dayName = dayNames[dateObj.getDay()]
      const day = dateObj.getDate()
      const time = `${startHour}:${startMinute}`

      // Siempre usar el mismo formato para mantener consistencia
      this.headerDateTimeTarget.textContent = `${dayName} ${day} - ${time}hs`
    }
  }

  updateStartMinutes() {
    const selectedHourOption = this.startHourTarget.selectedOptions[0];
    const bookedMinutes = JSON.parse(selectedHourOption.dataset.bookedMinutes || '[]');

    // Verificar si estamos en modo de edición (hay un event_id en el formulario)
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null;
    const currentEventId = isEditMode ? document.querySelector('input[name="appointment[event_id]"]').value : null;

    // Habilitar todos los minutos primero
    this.startMinuteTarget.querySelectorAll('option').forEach(option => {
      option.disabled = false;
    });

    // Deshabilitar los minutos que están ocupados
    bookedMinutes.forEach(slot => {
      // Para hora de inicio, solo habilitamos los minutos que son end time
      // y no son start time al mismo tiempo
      if (!slot.is_end || slot.is_start) {
        // En modo de edición, no deshabilitar si es el evento actual
        if (!isEditMode || slot.event_id !== currentEventId) {
          const minuteOption = this.startMinuteTarget.querySelector(`option[value="${slot.minute.toString().padStart(2, '0')}"]`);
          if (minuteOption) {
            minuteOption.disabled = true;
          }
        }
      }
    });

    // Verificar si todos los minutos están deshabilitados
    const allMinutesDisabled = Array.from(this.startMinuteTarget.querySelectorAll('option'))
      .filter(option => option.value !== '') // Excluir la opción vacía
      .every(option => option.disabled);

    // Si todos los minutos están deshabilitados, deshabilitar la hora
    if (allMinutesDisabled) {
      selectedHourOption.disabled = true;

      // Si la hora seleccionada está deshabilitada, seleccionar la primera hora disponible
      if (this.startHourTarget.selectedOptions[0].disabled) {
        const firstAvailableHour = this.startHourTarget.querySelector('option:not([disabled])');
        if (firstAvailableHour) {
          firstAvailableHour.selected = true;
          // Disparar el evento change para actualizar los minutos
          this.startHourTarget.dispatchEvent(new Event('change'));
        }
      }
    } else {
      selectedHourOption.disabled = false;
    }

    // Si el minuto seleccionado está deshabilitado, seleccionar el primer minuto disponible
    if (this.startMinuteTarget.selectedOptions[0].disabled) {
      const firstAvailableMinute = this.startMinuteTarget.querySelector('option:not([disabled])');
      if (firstAvailableMinute) {
        firstAvailableMinute.selected = true;
      }
    }

    this.validateStartTime();
  }

  updateEndMinutes() {
    const selectedHourOption = this.endHourTarget.selectedOptions[0];
    const bookedMinutes = JSON.parse(selectedHourOption.dataset.bookedMinutes || '[]');

    // Verificar si estamos en modo de edición (hay un event_id en el formulario)
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null;
    const currentEventId = isEditMode ? document.querySelector('input[name="appointment[event_id]"]').value : null;

    // Habilitar todos los minutos primero
    this.endMinuteTarget.querySelectorAll('option').forEach(option => {
      option.disabled = false;
    });

    // Deshabilitar los minutos que están ocupados
    bookedMinutes.forEach(slot => {
      // Para hora de fin, solo habilitamos los minutos que son start time
      // y no son end time al mismo tiempo
      if (!slot.is_start || slot.is_end) {
        // En modo de edición, no deshabilitar si es el evento actual
        if (!isEditMode || slot.event_id !== currentEventId) {
          const minuteOption = this.endMinuteTarget.querySelector(`option[value="${slot.minute.toString().padStart(2, '0')}"]`);
          if (minuteOption) {
            minuteOption.disabled = true;
          }
        }
      }
    });

    // Verificar si todos los minutos están deshabilitados
    const allMinutesDisabled = Array.from(this.endMinuteTarget.querySelectorAll('option'))
      .filter(option => option.value !== '') // Excluir la opción vacía
      .every(option => option.disabled);

    // Si todos los minutos están deshabilitados, deshabilitar la hora
    if (allMinutesDisabled) {
      selectedHourOption.disabled = true;

      // Si la hora seleccionada está deshabilitada, seleccionar la primera hora disponible
      if (this.endHourTarget.selectedOptions[0].disabled) {
        const firstAvailableHour = this.endHourTarget.querySelector('option:not([disabled])');
        if (firstAvailableHour) {
          firstAvailableHour.selected = true;
          // Disparar el evento change para actualizar los minutos
          this.endHourTarget.dispatchEvent(new Event('change'));
        }
      }
    } else {
      selectedHourOption.disabled = false;
    }

    // Si el minuto seleccionado está deshabilitado, seleccionar el primer minuto disponible
    if (this.endMinuteTarget.selectedOptions[0].disabled) {
      const firstAvailableMinute = this.endMinuteTarget.querySelector('option:not([disabled])');
      if (firstAvailableMinute) {
        firstAvailableMinute.selected = true;
      }
    }

    this.validateEndTime();
  }

  initializeAddButtons() {
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      // Limpiar espacios libres existentes
      const existingFreeSpaces = cell.querySelectorAll('.free-space')
      existingFreeSpaces.forEach(space => space.remove())

      const appointments = cell.querySelectorAll('.appointment-badge')
      const currentHour = parseInt(cell.dataset.hour)
      const currentDate = cell.dataset.date

      // Si no hay citas en esta celda, no crear espacios libres
      // porque ya se muestra el botón principal de agregar
      if (appointments.length === 0) {
        return
      }

      // Obtener los slots ocupados en esta celda
      const occupiedSlots = []

      appointments.forEach(apt => {
        const top = parseFloat(apt.style.top) || 0
        const height = parseFloat(apt.style.height) || 100

        // Validar que los valores sean números válidos
        if (!isNaN(top) && !isNaN(height) && height > 0) {
          occupiedSlots.push({
            start: top,
            end: top + height,
            source: 'current-cell'
          })
        }
      })

      // Buscar eventos que vienen de la hora anterior y se desbordan
      const previousHourCell = document.querySelector(`.calendar-cell[data-hour="${currentHour - 1}"][data-date="${currentDate}"]`)
      if (previousHourCell) {
        const previousAppointments = previousHourCell.querySelectorAll('.appointment-badge')
        previousAppointments.forEach(apt => {
          const prevTop = parseFloat(apt.style.top) || 0
          const prevHeight = parseFloat(apt.style.height) || 100

          // Si el evento se desborda (top + height > 100)
          if (prevTop + prevHeight > 100) {
            const overflowAmount = (prevTop + prevHeight) - 100

            occupiedSlots.push({
              start: 0,
              end: overflowAmount,
              source: 'previous-overflow'
            })
          }
        })
      }

      // Ordenar slots por posición de inicio
      occupiedSlots.sort((a, b) => a.start - b.start)

      // Fusionar slots superpuestos con tolerancia para evitar espacios muy pequeños
      const mergedSlots = []
      const tolerance = 2 // 2% de tolerancia para fusionar slots muy cercanos

      occupiedSlots.forEach(slot => {
        if (mergedSlots.length === 0) {
          mergedSlots.push(slot)
        } else {
          const lastSlot = mergedSlots[mergedSlots.length - 1]
          // Fusionar si hay superposición o si están muy cerca (dentro de la tolerancia)
          if (slot.start <= lastSlot.end + tolerance) {
            // Fusionar slots superpuestos o muy cercanos
            lastSlot.end = Math.max(lastSlot.end, slot.end)
          } else {
            mergedSlots.push(slot)
          }
        }
      })

      // Encontrar espacios libres
      const freeSpaces = []
      let currentPosition = 0

      mergedSlots.forEach(slot => {
        if (slot.start > currentPosition) {
          const spaceHeight = slot.start - currentPosition
          // Solo crear espacios libres si son lo suficientemente grandes (mínimo 15% = 9 minutos)
          if (spaceHeight >= 15) {
            freeSpaces.push({
              start: currentPosition,
              end: slot.start
            })
          }
        }
        currentPosition = Math.max(currentPosition, slot.end)
      })

      // Agregar el último espacio libre si existe y es lo suficientemente grande
      if (currentPosition < 100) {
        const finalSpaceHeight = 100 - currentPosition
        if (finalSpaceHeight >= 15) { // Mínimo 15% = 9 minutos
          freeSpaces.push({
            start: currentPosition,
            end: 100
          })
        }
      }

      // Crear áreas libres con botones de agregar
      freeSpaces.forEach(space => {
        const spaceHeight = space.end - space.start

        if (spaceHeight >= 25) { // Solo crear en espacios de al menos 25% (15 minutos)
          const freeSpace = document.createElement('div')
          freeSpace.className = 'free-space'
          freeSpace.style.top = `${space.start}%`
          freeSpace.style.height = `${spaceHeight}%`
          freeSpace.style.position = 'absolute'
          freeSpace.style.width = '100%'
          freeSpace.style.left = '0'
          freeSpace.style.zIndex = '0'

          const addButton = document.createElement('div')
          addButton.className = 'add-appointment-btn'
          addButton.innerHTML = '<i class="fas fa-plus"></i>'
          addButton.style.position = 'absolute'
          addButton.style.top = '50%'
          addButton.style.left = '50%'
          addButton.style.transform = 'translate(-50%, -50%)'

          // Copiar los data attributes del cell al botón
          const cellData = cell.dataset
          Object.keys(cellData).forEach(key => {
            addButton.dataset[key] = cellData[key]
          })

          // Calcular la hora específica basada en la posición del inicio del espacio libre
          const minuteOffset = Math.round((space.start / 100) * 60)
          const hour = String(currentHour).padStart(2, '0')
          const minute = String(minuteOffset).padStart(2, '0')
          addButton.dataset.hour = `${hour}:${minute}`

          // Agregar el evento click
          addButton.addEventListener('click', (e) => {
            e.stopPropagation()
            this.openModal({
              currentTarget: addButton,
              preventDefault: () => {},
              stopPropagation: () => {}
            })
          })

          freeSpace.appendChild(addButton)
          cell.appendChild(freeSpace)
        }
      })
    })
  }

  initializeDatePickerValidation() {
    if (this.hasDateFieldTarget) {
      const dateField = this.dateFieldTarget

      // Agregar evento para cuando se selecciona una fecha
      dateField.addEventListener('input', (e) => {
        this.validateDateInput(e.target.value)
      })
    }
  }

  validateDateInput(dateValue) {
    if (!dateValue) return

    const blockedDates = JSON.parse(this.outOfOfficeDatesTarget.value || '[]')
    const isBlocked = blockedDates.some(date => date === dateValue)

    if (isBlocked) {
      // Determinar si es fin de semana
      const [year, month, day] = dateValue.split('-').map(Number)
      const dateForWeekday = new Date(year, month - 1, day)
      const dayOfWeek = dateForWeekday.getDay()

      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

      if (isWeekend) {
        alert('No se pueden programar citas los sábados y domingos.')
      } else {
        alert('No se pueden programar citas en días que tienen eventos de todo el día.')
      }

      // Restaurar valor original
      this.dateFieldTarget.value = this.dateFieldTarget.dataset.originalValue
    } else {
      this.clearBookedSlots()
      this.fetchBookedSlotsForDate(dateValue)
    }
  }

  clearBookedSlots() {
    // Reset all hour options to have no booked minutes
    if (this.hasStartHourTarget) {
      this.startHourTarget.querySelectorAll('option').forEach(option => {
        option.dataset.bookedMinutes = '[]'
        option.disabled = false
      })
    }
    if (this.hasEndHourTarget) {
      this.endHourTarget.querySelectorAll('option').forEach(option => {
        option.dataset.bookedMinutes = '[]'
        option.disabled = false
      })
    }

    // Re-enable all minute options
    this.updateStartMinutes()
    this.updateEndMinutes()
  }

  fetchBookedSlotsForDate(dateValue) {
    if (!dateValue) return
    if (!this.hasStartHourTarget || !this.hasEndHourTarget) return

    const calendarIdField = document.querySelector('input[name="calendar_id"]')
    if (!calendarIdField) return
    const calendarId = calendarIdField.value

    const eventIdField = document.querySelector('input[name="appointment[event_id]"]')
    const excludeEventId = eventIdField ? eventIdField.value : ''

    const params = new URLSearchParams({
      calendar_id: calendarId,
      date: dateValue
    })
    if (excludeEventId) {
      params.append('exclude_event_id', excludeEventId)
    }

    fetch(`/appointments/booked_slots_for_date?${params}`)
      .then(response => response.json())
      .then(data => {
        if (data.error) return

        this.applyBookedSlots(data.booked_slots || {})

        if (this.hasOutOfOfficeDatesTarget && data.out_of_office_dates) {
          this.outOfOfficeDatesTarget.value = JSON.stringify(data.out_of_office_dates)
        }

        this.updateStartMinutes()
        this.updateEndMinutes()
      })
      .catch(error => {
        console.error('Error fetching booked slots:', error)
      })
  }

  applyBookedSlots(bookedSlots) {
    // Update start hour options
    this.startHourTarget.querySelectorAll('option').forEach(option => {
      const hour = parseInt(option.value)
      if (isNaN(hour)) return
      const slots = bookedSlots[hour] || []
      option.dataset.bookedMinutes = JSON.stringify(slots)

      // Disable hour if all 15-min slots are booked
      const allBooked = [0, 15, 30, 45].every(minute =>
        slots.some(slot => (!slot.is_end || slot.is_start) && slot.minute === minute)
      )
      option.disabled = allBooked
    })

    // Update end hour options
    this.endHourTarget.querySelectorAll('option').forEach(option => {
      const hour = parseInt(option.value)
      if (isNaN(hour)) return
      const slots = bookedSlots[hour] || []
      option.dataset.bookedMinutes = JSON.stringify(slots)

      // Disable hour if all 15-min slots are booked
      const allBooked = [0, 15, 30, 45].every(minute =>
        slots.some(slot => (!slot.is_start || slot.is_end) && slot.minute === minute)
      )
      option.disabled = allBooked
    })
  }

  adjustEndTime(durationMinutes) {
    if (!this.hasStartHourTarget || !this.hasStartMinuteTarget ||
        !this.hasEndHourTarget || !this.hasEndMinuteTarget) return

    // Verificar si estamos en modo de edición
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    // Get current start time values
    const startHour = parseInt(this.startHourTarget.value)
    const startMinute = parseInt(this.startMinuteTarget.value)

    if (isNaN(startHour) || isNaN(startMinute)) return

    // Calculate new end time
    let endHour = startHour
    let endMinute = startMinute + durationMinutes

    // Adjust if minutes overflow
    while (endMinute >= 60) {
      endHour += 1
      endMinute -= 60
    }

    // Format the values
    const endHourStr = endHour.toString().padStart(2, '0')
    const endMinuteStr = endMinute.toString().padStart(2, '0')

    // Check if the new end time is available
    const endHourOption = this.endHourTarget.querySelector(`option[value="${endHourStr}"]`)
    const endMinuteOption = this.endMinuteTarget.querySelector(`option[value="${endMinuteStr}"]`)

    if (endHourOption && !endHourOption.disabled && endMinuteOption && !endMinuteOption.disabled) {
      // Set the new end time
      this.endHourTarget.value = endHourStr
      this.endMinuteTarget.value = endMinuteStr

      // Trigger change events to update the UI
      this.endHourTarget.dispatchEvent(new Event('change'))
      this.endMinuteTarget.dispatchEvent(new Event('change'))
    } else {
      // Solo mostrar alert si NO estamos en modo de edición
      if (!isEditMode) {
        alert(`Este horario no está disponible. Hay eventos agendados que impiden programar la cita hasta las ${endHourStr}:${endMinuteStr}hs.`)
      }

      // Find the next available time slot
      this.findNextAvailableEndTime(startHour, startMinute, durationMinutes)
    }

    // Update hidden fields and header
    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()
  }

  findNextAvailableEndTime(startHour, startMinute, durationMinutes) {
    let currentHour = startHour
    let currentMinute = startMinute + durationMinutes

    // Adjust if minutes overflow
    while (currentMinute >= 60) {
      currentHour += 1
      currentMinute -= 60
    }

    // Update the end time with + duration minutes
    const hourStr = currentHour.toString().padStart(2, '0')
    const minuteStr = currentMinute.toString().padStart(2, '0')

    // Found available slot
    this.endHourTarget.value = hourStr
    this.endMinuteTarget.value = minuteStr

    // Trigger change events
    this.endHourTarget.dispatchEvent(new Event('change'))
    this.endMinuteTarget.dispatchEvent(new Event('change'))
  }

  validateFileSize(event) {
    const file = event.target.files[0]
    const maxSizeMB = parseInt(event.target.dataset.maxSize) || 10
    const maxSizeBytes = maxSizeMB * 1024 * 1024

    if (file && file.size > maxSizeBytes) {
      alert(`El archivo es demasiado grande. El tamaño máximo permitido es ${maxSizeMB}MB.`)
      event.target.value = ''
      return false
    }

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (file && !allowedTypes.includes(file.type)) {
      alert('Formato de archivo no válido. Solo se permiten archivos JPG, PNG y PDF.')
      event.target.value = ''
      return false
    }

    return true
  }

  // Toggle icon for collapsible sections
  toggleIcon(event) {
    const button = event.currentTarget
    const targetId = button.getAttribute('data-target')

    // Encontrar el icono específico basado en el target
    let icon = null
    if (targetId === '#moreInfo') {
      icon = this.element.querySelector('[data-appointments-target="moreInfoIcon"]')
    } else if (targetId === '#paymentBilling') {
      icon = this.element.querySelector('[data-appointments-target="paymentBillingIcon"]')
    }

    if (icon) {
      // Cambiar el icono inmediatamente basado en el estado actual
      const isCurrentlyPlus = icon.classList.contains('fa-plus-circle')
      const isCurrentlyChevronDown = icon.classList.contains('fa-chevron-down')

      if (isCurrentlyPlus) {
        // Si es plus, cambiar a minus (se está abriendo)
        icon.classList.remove('fa-plus-circle')
        icon.classList.add('fa-minus-circle')
      } else if (isCurrentlyChevronDown) {
        // Si es chevron-down, cambiar a chevron-up (se está abriendo)
        icon.classList.remove('fa-chevron-down')
        icon.classList.add('fa-chevron-up')
      } else if (icon.classList.contains('fa-minus-circle')) {
        // Si es minus, cambiar a plus (se está cerrando)
        icon.classList.remove('fa-minus-circle')
        icon.classList.add('fa-plus-circle')
      } else if (icon.classList.contains('fa-chevron-up')) {
        // Si es chevron-up, cambiar a chevron-down (se está cerrando)
        icon.classList.remove('fa-chevron-up')
        icon.classList.add('fa-chevron-down')
      }
    }
  }

  // Toggle receipt field visibility based on payment method
  toggleReceiptField(event) {
    const paymentMethod = event.target.value
    const receiptSection = document.getElementById('receiptSection')

    if (receiptSection) {
      if (paymentMethod === 'transferencia') {
        receiptSection.style.display = 'block'
        receiptSection.style.marginBottom = '1rem'
      } else {
        receiptSection.style.display = 'none'
        receiptSection.style.marginBottom = '0'
        // Clear the file input when hiding
        const fileInput = receiptSection.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.value = ''
        }
      }
    }
  }

  // Toggle invoice field visibility based on billing checkbox
  toggleInvoiceField(event) {
    const isBilled = event.target.checked
    const invoiceSection = document.getElementById('invoiceSection')

    if (invoiceSection) {
      if (isBilled) {
        invoiceSection.style.display = 'block'
      } else {
        invoiceSection.style.display = 'none'
        // Clear the file input when hiding
        const fileInput = invoiceSection.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.value = ''
        }
      }
    }
  }

  // Inicializar iconos de secciones colapsables
  initializeCollapsibleIcons() {
    ['#moreInfo', '#paymentBilling'].forEach(collapseId => {
      const collapseElement = this.element.querySelector(collapseId)
      if (collapseElement) {
        const button = this.element.querySelector(`[data-target="${collapseId}"]`)
        const icon = button ? button.querySelector('i') : null

        if (icon) {
          collapseElement.addEventListener('show.bs.collapse', () => {
            icon.classList.remove('fa-plus-circle')
            icon.classList.add('fa-minus-circle')
          })

          collapseElement.addEventListener('hide.bs.collapse', () => {
            icon.classList.remove('fa-minus-circle')
            icon.classList.add('fa-plus-circle')
          })

          // Set initial state
          if (collapseElement.classList.contains('show')) {
            icon.classList.remove('fa-plus-circle')
            icon.classList.add('fa-minus-circle')
          } else {
            icon.classList.remove('fa-minus-circle')
            icon.classList.add('fa-plus-circle')
          }
        }
      }
    })
  }

  toggleReceiptUpload(event) {
    event.preventDefault()
    const uploadSection = document.getElementById('receiptUploadSection')
    if (uploadSection) {
      uploadSection.style.display = uploadSection.style.display === 'none' ? 'flex' : 'none'
      uploadSection.style.marginTop = uploadSection.style.display === 'none' ? '0' : '1rem'
      uploadSection.style.marginBottom = uploadSection.style.display === 'none' ? '0' : '1rem'
    }
  }

  toggleInvoiceUpload(event) {
    event.preventDefault()
    const uploadSection = document.getElementById('invoiceUploadSection')
    if (uploadSection) {
      uploadSection.style.display = uploadSection.style.display === 'none' ? 'flex' : 'none'
      uploadSection.style.marginTop = uploadSection.style.display === 'none' ? '0' : '1rem'
      uploadSection.style.marginBottom = uploadSection.style.display === 'none' ? '0' : '1rem'
    }
  }

  // Inicializar collapse de Bootstrap
  initializeBootstrapCollapse() {
    // Asegurar que los collapses estén en el estado correcto inicialmente
    const collapsibleButtons = document.querySelectorAll('[data-toggle="collapse"]')
    collapsibleButtons.forEach(button => {
      const targetId = button.getAttribute('data-target')
      const isExpanded = button.getAttribute('aria-expanded') === 'true'

      // Encontrar el contenido específico basado en el target
      let content = null
      if (targetId === '#moreInfo') {
        content = document.getElementById('moreInfo')
      } else if (targetId === '#paymentBilling') {
        content = document.getElementById('paymentBilling')
      }

      if (content) {
        if (isExpanded) {
          content.style.display = 'block'
        } else {
          content.style.display = 'none'
        }
      }
    })
  }

  initializeLocationFields() {
    // Initialize location fields based on the checked radio button
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')
    if (locationInput) {
      try {
        this.updateLocation({ target: { dataset: { location: locationInput.value } } })
      } catch (error) {
        console.warn('Error initializing location fields:', error)
      }
    }
  }

  // Handle patient preselection when coming from patient creation
  handlePatientPreselection() {
    // Only proceed if we're in a form with patient search
    if (!this.element.querySelector('[data-patient-search-target]')) {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const selectedPatientId = urlParams.get('selected_patient_id')
    const fromPatientCreation = urlParams.get('from_patient_creation')

    if (selectedPatientId && fromPatientCreation) {

      // Get doctor email from the form
      const doctorEmail = document.querySelector('input[name="doctor_email"]')?.value || ''

      if (doctorEmail) {
        // Find the newly created patient and select it by ID
        const searchUrl = `/appointments/search_patients?doctor_email=${encodeURIComponent(doctorEmail)}&patient_id=${selectedPatientId}`

        fetch(searchUrl)
          .then(response => response.text())
          .then(html => {
            const resultsContainer = document.querySelector('[data-patient-search-target="results"]')
            if (resultsContainer) {
              resultsContainer.innerHTML = html
              resultsContainer.classList.remove('d-none')

              // Find and click the first patient result (should be the newly created one)
              const firstPatientResult = resultsContainer.querySelector('.patient-search-result')
              if (firstPatientResult) {
                firstPatientResult.click()
              }
            } else {
              console.error('Results container not found')
            }
          })
          .catch(error => {
            console.error('Error loading patient:', error)
          })
      } else {
        // Missing doctorEmail
      }
    }
  }

  // Auto-open modal when coming from patient creation
  autoOpenModalFromPatientCreation() {
    // Check if there's an element with auto-open data attribute
    const autoOpenElement = this.element.querySelector('[data-appointments-auto-open="true"]')

    if (autoOpenElement) {

      // Get the parameters from data attributes
      const date = autoOpenElement.dataset.appointmentsDate
      const hour = autoOpenElement.dataset.appointmentsHour
      const doctorEmail = autoOpenElement.dataset.appointmentsDoctorEmail
      let calendarId = autoOpenElement.dataset.appointmentsCalendarId
      const selectedPatientId = autoOpenElement.dataset.appointmentsSelectedPatientId

      // Fallback: if calendar_id is empty, use doctor_email as calendar_id
      if (!calendarId || calendarId.trim() === '') {
        calendarId = doctorEmail
      }

      // Check if all required parameters are present
      if (date && hour && doctorEmail && calendarId) {
        // Construct the URL for the modal
        const url = `/appointments/new?date=${date}&hour=${hour}&doctor_email=${doctorEmail}&calendar_id=${calendarId}&from_patient_creation=true&selected_patient_id=${selectedPatientId}`

        // Load the modal content
        this.loadModalContent(url)

        // Handle patient preselection after modal is loaded
        setTimeout(() => {
          this.handlePatientPreselection()
        }, 1000)
      } else {
        // Missing required parameters for auto-open
      }
    }
  }

  // Auto-open invoice modal
  autoOpenInvoiceModal() {
    // Check if there's an element with auto-open-invoice data attribute
    const autoOpenInvoiceElement = this.element.querySelector('[data-appointments-auto-open-invoice="true"]')

    if (autoOpenInvoiceElement) {

      // Get the parameters from data attributes
      const appointmentId = autoOpenInvoiceElement.dataset.appointmentsAppointmentId
      const doctorEmail = autoOpenInvoiceElement.dataset.appointmentsDoctorEmail
      const calendarId = autoOpenInvoiceElement.dataset.appointmentsCalendarId

      // Check if all required parameters are present
      if (appointmentId && doctorEmail && calendarId) {
        // Construct the URL for the edit modal with invoice modal open
        const url = `/appointments/${appointmentId}/edit?calendar_id=${calendarId}&doctor_email=${doctorEmail}&show_invoice_modal=true`

        // Load the modal content
        this.loadModalContent(url)
      } else {
        // Missing required parameters for invoice modal auto-open
      }
    }
  }

  addModalCloseListeners() {
    const modalElement = document.getElementById("appointment_modal")
    if (modalElement) {
      // Listen for Bootstrap modal hidden event
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.cleanupUrlParameters()
      })

      // Also listen for click on backdrop or escape key
      modalElement.addEventListener('click', (event) => {
        if (event.target === modalElement) {
          // Clicked on backdrop, modal will close
          setTimeout(() => {
            this.cleanupUrlParameters()
          }, 100)
        }
      })

      // Listen for close buttons and cancel buttons
      modalElement.addEventListener('click', (event) => {
        const target = event.target
        if (target.matches('.btn-close, .close, [data-dismiss="modal"], [data-bs-dismiss="modal"]') ||
            target.closest('.btn-close, .close, [data-dismiss="modal"], [data-bs-dismiss="modal"]')) {
          setTimeout(() => {
            this.cleanupUrlParameters()
          }, 100)
        }
      })
    }
  }

  // Métodos para manejo de precios
  initializePriceHandling() {
    // Manejar cambio entre precio automático y manual
    this.priceTypeRadios = document.querySelectorAll('input[name="price_type"]');
    this.manualPriceField = document.getElementById('manual_price_field');

    if (this.priceTypeRadios.length > 0) {
      // Inicializar valores de los campos hidden según el radio seleccionado
      const checkedRadio = document.querySelector('input[name="price_type"]:checked');
      if (checkedRadio) {
        this.handlePriceTypeChange({ target: checkedRadio });
      }

      this.priceTypeRadios.forEach(radio => {
        radio.addEventListener('change', this.handlePriceTypeChange.bind(this));
      });
    }

    // Actualizar precio calculado cuando cambie el paciente
    if (this.hasPatientIdTarget) {
      this.patientIdTarget.addEventListener('change', this.updateCalculatedPriceDisplay.bind(this));
    }
  }

  handlePriceTypeChange(event) {
    const useAutomaticField = document.getElementById('use_automatic_price_field');
    const useSavedAutomaticField = document.getElementById('use_saved_automatic_price_field');

    if (event.target.value === 'manual') {
      this.manualPriceField.style.display = 'block';
      if (useAutomaticField) useAutomaticField.value = 'false';
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'false';

      // Enfocar el input cuando se selecciona precio manual
      const priceInput = this.manualPriceField.querySelector('input[type="number"]');
      if (priceInput) {
        setTimeout(() => priceInput.focus(), 100);
      }
    } else if (event.target.value === 'saved_automatic') {
      this.manualPriceField.style.display = 'none';
      if (useAutomaticField) useAutomaticField.value = 'false';
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'true';
    } else {
      // 'automatic' - usar precio automático actual
      this.manualPriceField.style.display = 'none';
      if (useAutomaticField) useAutomaticField.value = 'true';
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'false';
    }
  }

  // Método para actualizar el display del precio calculado
  updateCalculatedPriceDisplay() {
    const doctorId = this.data.get('doctor-id');
    const patientId = this.patientIdTarget?.value;

    if (doctorId && patientId) {
      fetch(`/appointments/consultation_price_info?doctor_id=${doctorId}&patient_id=${patientId}`)
        .then(response => response.json())
        .then(data => {
          const displayElement = document.querySelector('[data-appointments-target="calculatedPriceDisplay"]');
          if (displayElement) {
            if (data.price && data.price > 0) {
              displayElement.textContent = `$${Math.round(data.price)}`;
              displayElement.className = ''; // Limpiar clases
            } else {
              displayElement.textContent = 'El doctor debe configurar los precios';
              displayElement.className = 'text-danger';
            }
          }
        })
        .catch(error => {
          console.error('Error al obtener precio calculado:', error);
          const displayElement = document.querySelector('[data-appointments-target="calculatedPriceDisplay"]');
          if (displayElement) {
            displayElement.textContent = 'Error al calcular precio';
            displayElement.className = 'text-danger';
          }
        });
    }
  }
}
